import React, { useState } from "react";
import { Scale, CheckCircle2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const RULES = [
  {
    title: "Portaria MTP 2.175/2022 — NR-6 (EPI)",
    what: "Define as obrigações da empresa: fornecer EPI adequado ao risco, com CA válido, gratuitamente, registrar a entrega e orientar o uso.",
    how: [
      "Catálogo de EPIs com número e validade do CA, fabricante e categoria.",
      "Ficha individual de entrega por colaborador (PDF) com data, quantidade e motivo.",
      "Confirmação do recebimento por biometria (foto facial) ou assinatura em tela.",
      "Alertas de CA vencido ou a vencer no painel e no sino de notificações.",
    ],
  },
  {
    title: "Portaria MTP 672/2021 — Procedimentos de EPI e CA",
    what: "Regula a emissão e validade do Certificado de Aprovação (CA) e a rastreabilidade do produto.",
    how: [
      "Lote do fabricante obrigatório em toda entrega (rastreabilidade em auditoria).",
      "Validade do produto/lote: EPI vencido fisicamente tem a entrega BLOQUEADA.",
      "CA vencido: entrega só é liberada se a Nota Fiscal de compra for anterior ao vencimento do CA; compra nova com CA vencido é recusada.",
    ],
  },
  {
    title: "Portaria MTE 1.369/2024 — Categorias de risco",
    what: "Classifica os EPIs em Categoria I, II ou III conforme a gravidade do risco protegido.",
    how: [
      "Campo obrigatório de Categoria (I, II, III) no catálogo e na entrega.",
      "Categoria III com item complementar cadastrado (ex.: cinturão → talabarte): o sistema exige confirmar a entrega conjunta, evitando proteção incompleta.",
    ],
  },
  {
    title: "Portaria MTE 122/2025 — Atualizações de validade e controle",
    what: "Reforça o controle de validade do EPI e do CA no fornecimento ao trabalhador.",
    how: [
      "Travas automáticas de validade do produto e do CA no momento do registro.",
      "Painel de vencimentos e alertas para reposição antes do prazo.",
    ],
  },
  {
    title: "Portaria SEPRT 211/2019 — Registro eletrônico e integridade",
    what: "Permite o registro eletrônico de entrega de EPI desde que garanta autenticidade, integridade e guarda dos dados.",
    how: [
      "Cada confirmação gera um código de auditoria (hash SHA-256) e um log com data/hora e método.",
      "Entrega confirmada fica somente leitura: não pode ser editada nem excluída.",
      "Erros são corrigidos por ESTORNO: o registro original é marcado [ESTORNADO] e uma linha de contrapartida é criada — nenhuma assinatura ou foto é apagada.",
      "Após 3 falhas na captura biométrica, a assinatura em tela é liberada como contingência e isso fica registrado.",
    ],
  },
  {
    title: "eSocial — Evento S-2240 (condições ambientais / EPI)",
    what: "Informa ao governo os EPIs fornecidos ao trabalhador exposto a agentes nocivos.",
    how: [
      "Toda entrega confirmada gera automaticamente o JSON S-2240 (CPF, matrícula, CA, descrição eSocial e requisitos de uso), pronto para a mensageria.",
      "Botão eSocial em cada entrega para visualizar e copiar o JSON.",
    ],
  },
];

export function LegalCompliance() {
  const [open, setOpen] = useState(false);
  return (
    <>
      <Button variant="outline" onClick={() => setOpen(true)} className="border-[#134074]/30 text-[#134074]" data-testid="ppe-legal-btn">
        <Scale className="mr-1.5 h-4 w-4" /> Conformidade Legal
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-h-[88vh] overflow-y-auto sm:max-w-[720px]" data-testid="ppe-legal-dialog">
          <DialogHeader><DialogTitle className="text-[#0B2545]">Como o VIGIA atende às portarias de EPI</DialogTitle></DialogHeader>
          <p className="text-sm text-slate-500">Descritivo técnico simplificado. Observação: o controle de estoque não está ativo; as travas são aplicadas no registro da entrega.</p>
          <div className="space-y-4">
            {RULES.map((r, i) => (
              <div key={r.title} className="rounded-xl border border-slate-200 p-4" data-testid={`ppe-legal-item-${i}`}>
                <p className="font-semibold text-[#0B2545]">{r.title}</p>
                <p className="mt-1 text-sm text-slate-600"><b>O que exige:</b> {r.what}</p>
                <p className="mt-2 text-xs font-semibold uppercase tracking-wider text-emerald-700">Como o sistema atende</p>
                <ul className="mt-1 space-y-1">
                  {r.how.map((h) => <li key={h} className="flex gap-2 text-sm text-slate-700"><CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-emerald-600" />{h}</li>)}
                </ul>
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
